import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { SquarePen, Download, Settings2, Tag, ChevronRight, ChevronDown, CircleHelp, Funnel, FunnelX } from 'lucide-react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import CasesSidebar from './cases/CasesSidebar';
import EvidenceManager from './EvidenceManager';
import NewCaseModal from './cases/CaseModal';
import AuthPage from './auth/AuthPage';
import { useAuth } from '../firebase/AuthContext';
import FirebaseService from '../services/FirebaseService';
import EvidenceDataService from '../services/EvidenceDataService';
import DocumentSetService from '../services/DocumentSetService';
import IconButton from './ui/IconButton';
import DocumentSetPill from './document-sets/DocumentSetPill';
import EmptyCaseState from './cases/EmptyCaseState';
import TrialBanner from './subscription/TrialBanner';
import PopoverWrapper from './ui/PopoverWrapper';
import ScrollContainer from './ui/ScrollContainer';
import SearchInput from './ui/SearchInput';
import { ReactComponent as Briefcase } from '../assets/icons/briefcase.svg';
import {
  trackCaseCreated,
  trackCaseOpened,
  trackCaseEdited,
  trackCaseDeleted,
  trackStampConfigChanged,
  trackDocumentSetCreated,
  trackDocumentSetRenamed,
  trackDocumentSetArchived,
  trackDocumentSetDeleted
} from '../utils/analytics';
import { incrementActionCount, useActionCounts } from '../hooks/useGuide';
import TagGuide from './ui/TagGuide';
import { GuideForceProvider } from '../contexts/GuideForceContext';

/**
 * Editable Case Name Component with Edit Icon + Split Export Button
 */
const CaseName = ({
  caseName,
  onOpenEditModal,
  // Export button props
  isExporting,
  canExport,
  onExportCombinedPdf,
  selectedSet = null,
  documentSets = [],
  onSelectSet,
  inTagTeachingMode = false,
  keepDropdownOpenForGuide = false,
  onStartTagGuide,
  hasErrors = false,
  filterErrors = false,
  onToggleFilterErrors,
}) => {
  const dropdownContentRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // 'exportAll' | 'exportByTag' | null — null means nothing chosen yet (teaching mode)
  const [selectedOption, setSelectedOption] = useState('exportAll');
  const [tagSearch, setTagSearch] = useState('');
  // Track hover on first row to color the arrow (arrow is at top for side="bottom")
  const [isFirstRowHovered, setIsFirstRowHovered] = useState(false);
  // Independent 3D raise state for each split-button half
  const [isLeftHovered, setIsLeftHovered] = useState(false);
  const [isLeftActive, setIsLeftActive] = useState(false);
  const [isRightHovered, setIsRightHovered] = useState(false);
  const [isRightActive, setIsRightActive] = useState(false);
  // When the dropdown is open and the download side is clicked, both halves animate in sync
  const [syncedPress, setSyncedPress] = useState(false);
  // Teaching mode: becomes true once the user has made a selection from the forced dropdown.
  // Persists across case switches (no key= on CaseName, so state survives re-renders).
  const [teachingUnlocked, setTeachingUnlocked] = useState(false);

  // Fix 1: after mousedown+mouseup, hover should not re-activate until the mouse leaves
  // and re-enters. This prevents the button from immediately popping back up after a click.
  // We clear isLeftHovered on mousedown, then onMouseEnter only fires again after a mouseleave.
  const pendingHoverReentryRef = useRef(false);

  // Fix 2: Radix runs a close animation before unmounting the portal (~150ms). When the
  // portal finally unmounts the browser fires a synthetic mouseenter on whatever is now
  // under the cursor — often the settings button. Ignore mouseenter on the right half for
  // a short window after the dropdown starts closing so that spurious event is absorbed.
  const ignoreRightHoverRef = useRef(false);
  const ignoreRightHoverTimerRef = useRef(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearTimeout(ignoreRightHoverTimerRef.current);
    };
  }, []);

  // Returns inline transform + box-shadow for the 3D raise effect.
  // isOpen keeps the right half raised while the dropdown is open.
  const get3dStyle = (isHovered, isActive, isOpen = false) => {
    const transition = 'transform 200ms cubic-bezier(.2,-0.8,0.32,1.8), box-shadow 200ms cubic-bezier(.2,-0.8,0.32,1.8)';
    if (isActive) {
      return { transform: 'translateY(-2px)', boxShadow: '0 2px 0 0 #8F99A3', transition, willChange: 'transform' };
    } else if (isHovered || isOpen) {
      return { transform: 'translateY(-4px)', boxShadow: '0 4px 0 0 #8F99A3', transition, willChange: 'transform' };
    } else {
      return { transform: 'translateY(0px)', boxShadow: '0 0 0 0 #8F99A3', transition, willChange: 'transform' };
    }
  };

  const filteredSets = useMemo(() => {
    const active = documentSets.filter(s => !s.archived);
    if (!tagSearch.trim()) return active;
    const query = tagSearch.toLowerCase();
    return active.filter(s => s.name.toLowerCase().includes(query));
  }, [documentSets, tagSearch]);

  // Whether the teaching interception is currently active
  const teachingActive = inTagTeachingMode && !teachingUnlocked;

  const handleOpenChange = (open) => {
    setIsDropdownOpen(open);
    if (open) {
      // In teaching mode, show nothing pre-selected so the user must make a conscious choice
      setSelectedOption(teachingActive ? null : (selectedSet ? 'exportByTag' : 'exportAll'));
      setTagSearch('');
      setIsFirstRowHovered(false);
    } else {
      // Absorb the spurious mouseenter that fires on the settings button when Radix's
      // close animation finishes and the portal unmounts (~150ms). 300ms is enough margin.
      setIsRightHovered(false);
      ignoreRightHoverRef.current = true;
      clearTimeout(ignoreRightHoverTimerRef.current);
      ignoreRightHoverTimerRef.current = setTimeout(() => {
        ignoreRightHoverRef.current = false;
      }, 300);
    }
  };

  const handleSelectExportAll = () => {
    setSelectedOption('exportAll');
    if (selectedSet) onSelectSet(null);
    // Teaching: unlock the button and auto-close so the user can proceed to download.
    // Route through handleOpenChange (not setIsDropdownOpen directly) so the right-hover
    // cleanup always runs and the settings button doesn't get stuck raised.
    if (teachingActive) {
      setTeachingUnlocked(true);
      handleOpenChange(false);
    }
  };

  const handleSelectExportByTag = () => {
    setSelectedOption('exportByTag');
  };

  const handleTagSelect = (setId) => {
    onSelectSet(setId);
    // Teaching: selecting a specific tag counts as a conscious choice — unlock and close
    if (teachingActive) {
      setTeachingUnlocked(true);
      handleOpenChange(false);
    }
  };

  const isLeftDisabled = isExporting || !canExport;

  // Arrow at top of popover (side="bottom") should match the first row when it's active.
  // In teaching mode (selectedOption === null) default to the darker color.
  const arrowColorClass = (selectedOption === 'exportAll' || isFirstRowHovered)
    ? 'bg-app-gray-700 fill-app-gray-700'
    : 'bg-app-gray-800 fill-app-gray-800';

  const settingsButton = (
    <button
      className="flex items-center px-3 h-[38px] bg-transparent border-none text-app-white hover:bg-white/10 active:bg-white/20 transition-colors duration-app-fast cursor-pointer flex-shrink-0"
      style={{ outline: 'none' }}
    >
      <Settings2 size={14} />
    </button>
  );

  return (
    <div className="flex items-center justify-between gap-4">
      {/* Left side - Case name + edit icon */}
      <div className="flex items-center gap-app-md">
        <h1 className="text-2xl font-bold text-app-gray-900">
          {caseName}
        </h1>
        <IconButton
          onClick={onOpenEditModal}
          title="Edit case details and stamp configuration"
          size="lg"
        >
          <SquarePen strokeWidth={2} />
        </IconButton>
        {selectedSet && (
          <div style={{ transform: 'scale(1.15)' }}>
            <DocumentSetPill
              documentSet={selectedSet}
              showRemove={false}
              isReadOnly={true}
            />
          </div>
        )}
      </div>

      {/* Right side — split export button, always visible. The download (left) half flips to an
          error-filter toggle when the current export scope (selected tag group, or the whole case
          when no tag is selected) contains error docs. The settings (right) half stays available,
          so filtering to a clean tag group re-enables a normal export. */}
      <div
        className="flex-shrink-0 flex items-center text-app-white text-app-sm font-normal h-[38px] select-none bg-app-gray-900 rounded-[19px]"
      >
        {/* Left half: main export action — raises independently. Divider lives here so it lifts with this half. */}
        <div
          className="rounded-l-[19px] overflow-hidden h-[38px] flex items-center bg-app-gray-900"
          style={get3dStyle(isLeftHovered, isLeftActive)}
          onMouseEnter={() => {
            // Only activate hover on a genuine re-entry after a click (not the same hover
            // that was already present when mousedown fired).
            if (!pendingHoverReentryRef.current) setIsLeftHovered(true);
          }}
          onMouseLeave={() => {
            setIsLeftHovered(false);
            setIsLeftActive(false);
            setSyncedPress(false);
            pendingHoverReentryRef.current = false; // real leave — next entry is genuine
          }}
          onMouseDown={() => {
            // Clear hover immediately so the button doesn't look raised while pressed,
            // and require a leave+re-enter before hover can activate again.
            setIsLeftHovered(false);
            pendingHoverReentryRef.current = true;
            setIsLeftActive(true);
            if (isDropdownOpen && !teachingActive) setSyncedPress(true);
          }}
          onMouseUp={() => {
            setIsLeftActive(false);
            setSyncedPress(false);
            // pendingHoverReentryRef stays true — hover won't re-show until mouse leaves
          }}
        >
          <button
            onClick={() => {
              // When the current export scope has error docs, the left half is the filter toggle,
              // not the download action — so a broken scope can't be exported and the user is
              // nudged to the error docs instead.
              if (hasErrors) {
                onToggleFilterErrors?.();
                return;
              }
              if (teachingActive) {
                handleOpenChange(true);
              } else {
                onExportCombinedPdf();
                // Consume the teaching unlock so the next download requires the flow again
                if (inTagTeachingMode) {
                  setTeachingUnlocked(false);
                  setSelectedOption(null);
                }
              }
            }}
            // In filter mode the button is always actionable; only the download action respects canExport.
            disabled={hasErrors ? false : isLeftDisabled}
            className="flex items-center gap-2 px-app-lg h-full bg-transparent border-none text-app-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            style={{ outline: 'none', fontWeight: 'normal', fontSize: '0.875rem' }}
          >
            {hasErrors ? (
              <>
                {filterErrors ? <FunnelX size={16} /> : <Funnel size={16} />}
                {filterErrors ? 'Fix later' : 'Fix docs'}
              </>
            ) : (
              <>
                <Download size={16} />
                {isExporting
                  ? 'Exporting...'
                  : selectedSet
                    ? 'Download Tag Group'
                    : 'Download All'
                }
              </>
            )}
          </button>
        </div>

        {/* Right half: settings dropdown trigger — raises independently, stays raised while open */}
        <div
          className="rounded-r-[19px] overflow-hidden h-[38px] flex items-center bg-app-gray-900 border-l border-app-gray-600"
          style={
            (isDropdownOpen || keepDropdownOpenForGuide) && !syncedPress
              // Pinned raised style whenever the popover is visually open — either a normal
              // open (isDropdownOpen) or held open behind the tag guide (keepDropdownOpenForGuide).
              // Without the latter, pressing the ? button clears isDropdownOpen while the popover
              // stays up, so the trigger would drop and resume hover-bouncing under the guide.
              ? get3dStyle(false, false, true)
              : get3dStyle(isRightHovered, isRightActive || syncedPress, false)
          }
          onMouseEnter={() => { if (!ignoreRightHoverRef.current) setIsRightHovered(true); }}
          onMouseLeave={() => { setIsRightHovered(false); setIsRightActive(false); }}
          onMouseDown={() => setIsRightActive(true)}
          onMouseUp={() => setIsRightActive(false)}
        >
        <PopoverWrapper
          trigger={settingsButton}
          open={isDropdownOpen || keepDropdownOpenForGuide}
          onOpenChange={handleOpenChange}
          theme="dark"
          side="bottom"
          align="end"
          sideOffset={8}
          className="overflow-hidden"
          arrowColorClass={arrowColorClass}
        >
          <div ref={dropdownContentRef} className="w-[240px]">
            {/* Row 1: Export all case docs */}
            <div
              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors duration-app-fast ${
                selectedOption === 'exportAll' ? 'bg-app-gray-700' : 'hover:bg-app-gray-700'
              }`}
              onClick={handleSelectExportAll}
              onMouseEnter={() => setIsFirstRowHovered(true)}
              onMouseLeave={() => setIsFirstRowHovered(false)}
            >
              <Briefcase
                className="w-4 h-4 text-app-white flex-shrink-0"
                style={selectedOption === 'exportAll' ? { strokeWidth: '2px' } : {}}
              />
              <span className={`text-app-sm text-app-white ${selectedOption === 'exportAll' ? 'font-semibold' : ''}`}>
                Export all case docs
              </span>
            </div>

            {/* Row 2: Export by tag */}
            <div
              className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors duration-app-fast ${
                selectedOption === 'exportByTag' ? 'bg-app-gray-700' : 'hover:bg-app-gray-700'
              }`}
              onClick={handleSelectExportByTag}
            >
              <Tag
                size={16}
                strokeWidth={selectedOption === 'exportByTag' ? 2.5 : 2}
                className="text-app-white flex-shrink-0"
              />
              <span className={`text-app-sm text-app-white ${selectedOption === 'exportByTag' ? 'font-semibold' : ''}`}>
                Export by tag
              </span>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const el = dropdownContentRef.current ?? e.currentTarget;
                  handleOpenChange(false);
                  onStartTagGuide?.(el, true);
                }}
                className="ml-app-2xs p-0.5 text-app-gray-500 hover:text-app-white transition-colors flex-shrink-0"
                title="Learn about tags"
              >
                <CircleHelp size={13} />
              </button>

              <div className="ml-auto">
                {selectedOption === 'exportByTag'
                  ? <ChevronDown size={14} className="text-app-gray-400 flex-shrink-0" />
                  : <ChevronRight size={14} className="text-app-gray-500 flex-shrink-0" />
                }
              </div>
            </div>

            {/* Inline tag selection — expands below row 2 when selected */}
            {selectedOption === 'exportByTag' && (
              <div className="border-t border-app-gray-700">
                {/* Search */}
                <div className="px-2 pt-2 pb-1">
                  <SearchInput
                    searchTerm={tagSearch}
                    onSearchChange={setTagSearch}
                    dark
                    placeholder="Search tags..."
                  />
                </div>
                {/* Radio list */}
                <RadioGroup.Root
                  value={selectedSet?.id || ''}
                  onValueChange={handleTagSelect}
                  className="pb-1"
                >
                  <ScrollContainer
                    className="max-h-[200px]"
                    scrollbarColors={{ normal: '#525C65', hover: '#707A84' }}
                  >
                    {filteredSets.length === 0 ? (
                      <div className="px-3 py-2 text-app-xs text-app-gray-500">
                        {tagSearch ? 'No matching tags' : 'No tags available'}
                      </div>
                    ) : (
                      filteredSets.map(set => (
                        <label
                          key={set.id}
                          htmlFor={`export-tag-${set.id}`}
                          className="flex items-center gap-3 px-3 py-[7px] cursor-pointer hover:bg-app-gray-700 transition-colors duration-app-fast"
                        >
                          <RadioGroup.Item
                            value={set.id}
                            id={`export-tag-${set.id}`}
                            className="w-comp-xl h-comp-xl rounded-full border border-app-gray-400 hover:border-app-gray-200 data-[state=checked]:border-app-gray-200 bg-app-gray-800 transition-all duration-200 flex items-center justify-center relative flex-shrink-0 cursor-pointer"
                          >
                            <div
                              className="w-comp-md h-comp-md rounded-full bg-app-gray-200 transition-transform duration-200 ease-out"
                              style={{ transform: selectedSet?.id === set.id ? 'scale(1)' : 'scale(0)' }}
                            />
                          </RadioGroup.Item>
                          <DocumentSetPill
                            documentSet={set}
                            showRemove={false}
                            isReadOnly={true}
                          />
                        </label>
                      ))
                    )}
                  </ScrollContainer>
                </RadioGroup.Root>
              </div>
            )}
          </div>
        </PopoverWrapper>
        </div>
      </div>
    </div>
  );
};

/**
 * No Case Selected Component
 */
const NoCaseSelected = ({ onCreateNewCase, hasCases, newCaseButtonRef, setShouldHighlightNewCaseButton }) => {
  if (!hasCases) {
    return (
      <EmptyCaseState 
        newCaseButtonRef={newCaseButtonRef}
        onButtonHighlight={(highlight) => {
          console.log('EmptyCaseState calling onButtonHighlight with:', highlight);
          setShouldHighlightNewCaseButton(highlight);
        }}
      />
    );
  }
  
  // Otherwise show the simple "select a case" message
  return (
    <div className="text-center" style={{ color: '#272838' }}>
      <h2 className="text-xl font-semibold mb-4">No Case Selected</h2>
      <p>Please select a case from the sidebar to get started.</p>
    </div>
  );
};

/**
 * Main Case Manager Component
 */
const CaseManager = () => {
  const { currentUser, logout } = useAuth();
  
  // State for case management
  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseIdState] = useState(() => {
    return localStorage.getItem('selectedCaseId') || null;
  });
  const selectedCase = cases.find(c => c.id === selectedCaseId) || null;
  const [isLoading, setIsLoading] = useState(true);
  const [isNewCaseModalOpen, setIsNewCaseModalOpen] = useState(false);
  const [isEditCaseModalOpen, setIsEditCaseModalOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedSetId, setSelectedSetId] = useState(null);

  // Per-case UI preference: sets column collapse state
  const [isSetsCollapsed, setIsSetsCollapsed] = useState(() => {
    if (!selectedCaseId) return false;
    const stored = localStorage.getItem(`setsCollapsed_${selectedCaseId}`);
    return stored === 'true';
  });

  // Update collapse state when case changes
  useEffect(() => {
    if (selectedCaseId) {
      const stored = localStorage.getItem(`setsCollapsed_${selectedCaseId}`);
      setIsSetsCollapsed(stored === 'true');
    }
  }, [selectedCaseId]);

  // Save collapse state to localStorage when it changes
  const handleSetCollapsedChange = useCallback((collapsed) => {
    setIsSetsCollapsed(collapsed);
    if (selectedCaseId) {
      localStorage.setItem(`setsCollapsed_${selectedCaseId}`, String(collapsed));
    }
  }, [selectedCaseId]);

  const isUserVerified = useCallback(() => {
    if (!currentUser) return false;
    
    // If email is verified, user is good to go
    if (currentUser.emailVerified) return true;
    
    // Check if user has signed in with Google
    // Google sign-ins are trusted since Google verifies emails
    const hasGoogleProvider = currentUser.providerData?.some(
      provider => provider.providerId === 'google.com'
    );
    
    if (hasGoogleProvider) {
      console.log('CaseManager - User signed in with Google, bypassing email verification');
      return true;
    }
    
    return false;
  }, [currentUser]);
  
  // Export state - moved from EvidenceManager
  const [isExporting, setIsExporting] = useState(false);
  const [canExport, setCanExport] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [filterErrors, setFilterErrors] = useState(false);

  // Action counts used to drive the tag teaching mode on the download button
  const { counts: teachingCounts, loaded: teachingCountsLoaded } = useActionCounts('exportDocs', 'createTagInMainFlow');
  // Teaching mode: user has exported at least once but hasn't yet created a tag in the main flow
  const inTagTeachingMode = teachingCountsLoaded
    && teachingCounts.exportDocs >= 1
    && teachingCounts.createTagInMainFlow === 0;

  // Stamp configuration state - moved to CaseManager level
  const [stampConfig, setStampConfig] = useState({
    partyType: 'petitioner',
    customPartyType: '',
    exhibitType: 'numbers',
    startingValue: '1',
    color: '#E2DA74'
  });

  const [shouldHighlightNewCaseButton, setShouldHighlightNewCaseButton] = useState(false);

  const exportTriggerRef = useRef();

  const newCaseButtonRef = useRef(null);

  const setSelectedCaseId = useCallback((caseId) => {
    console.log(`Setting selected case ID to: ${caseId}`);
    setSelectedCaseIdState(caseId);
    
    if (caseId) {
      try {
        localStorage.setItem('selectedCaseId', caseId);
        console.log(`Saved selected case ID to localStorage: ${caseId}`);
      } catch (e) {
        console.error('Failed to save case ID to localStorage:', e);
      }
    } else {
      try {
        localStorage.removeItem('selectedCaseId');
        console.log('Removed selected case ID from localStorage');
      } catch (e) {
        console.error('Failed to remove case ID from localStorage:', e);
      }
    }
  }, []);

  const handleSidebarCollapsedChange = useCallback((isCollapsed) => {
    setIsSidebarCollapsed(isCollapsed);
    
    // Update CSS custom property for scrollbar margin
    const sidebarWidth = isCollapsed ? '48px' : '224px';
    document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);
  }, []);

  // NEW: Get the selected document set object
  const selectedSet = useMemo(() => {
    if (!selectedSetId || !selectedCase) return null;
    const sets = selectedCase.documentSets || [];
    return sets.find(s => s.id === selectedSetId) || null;
  }, [selectedSetId, selectedCase]);

  const handleSelectSet = useCallback((setId) => {
    // Toggle selection: if clicking same set, deselect it
    setSelectedSetId(prev => prev === setId ? null : setId);
  }, []);

  const handleToggleArchiveSet = useCallback(async (setId) => {
    if (!selectedCaseId) return;

    try {
      const newArchivedState = await DocumentSetService.toggleArchiveDocumentSet(selectedCaseId, setId);

      // Track archive/unarchive action
      trackDocumentSetArchived(setId, newArchivedState);
      if (currentUser?.uid) {
        await incrementActionCount(currentUser.uid, newArchivedState ? 'archiveTag' : 'restoreTag');
      }

      // UPDATE LOCAL STATE
      setCases(prev => prev.map(c => {
        if (c.id === selectedCaseId) {
          return {
            ...c,
            documentSets: (c.documentSets || []).map(s =>
              s.id === setId ? { ...s, archived: newArchivedState } : s
            )
          };
        }
        return c;
      }));
    } catch (error) {
      console.error('Error toggling lock:', error);
    }
  }, [selectedCaseId, currentUser?.uid]);

  const handleDeleteSet = useCallback(async (setId) => {
    if (!selectedCaseId) return;

    try {
      await DocumentSetService.deleteDocumentSet(selectedCaseId, setId);

      // Track deletion
      trackDocumentSetDeleted(setId);

      if (selectedSetId === setId) {
        setSelectedSetId(null);
      }

      // UPDATE LOCAL STATE
      setCases(prev => prev.map(c => {
        if (c.id === selectedCaseId) {
          return {
            ...c,
            documentSets: (c.documentSets || []).filter(s => s.id !== setId)
          };
        }
        return c;
      }));
    } catch (error) {
      console.error('Error deleting set:', error);
    }
  }, [selectedCaseId, selectedSetId]);

  const handleUpdateSetName = useCallback(async (setId, newName) => {
    if (!selectedCaseId) return;

    try {
      await DocumentSetService.renameDocumentSet(selectedCaseId, setId, newName);

      // Track rename action
      trackDocumentSetRenamed(setId);

      // UPDATE LOCAL STATE
      setCases(prev => prev.map(c => {
        if (c.id === selectedCaseId) {
          return {
            ...c,
            documentSets: (c.documentSets || []).map(s =>
              s.id === setId ? { ...s, name: newName, lastModified: new Date() } : s
            )
          };
        }
        return c;
      }));
    } catch (error) {
      console.error('Error updating set name:', error);
    }
  }, [selectedCaseId]);

  const handleCreateDocumentSet = useCallback(async (name, color) => {
    if (!selectedCaseId) return;

    try {
      const newSet = await DocumentSetService.createDocumentSet(selectedCaseId, name, color);

      // Track creation with whether custom color was provided
      trackDocumentSetCreated(newSet.id, name, !!color);

      // UPDATE LOCAL STATE
      setCases(prev => prev.map(c => {
        if (c.id === selectedCaseId) {
          return {
            ...c,
            documentSets: [...(c.documentSets || []), newSet]
          };
        }
        return c;
      }));

      return newSet;
    } catch (error) {
      console.error('Error creating document set:', error);
    }
  }, [selectedCaseId]);

  // CLEAR SELECTED SET when case changes:
  useEffect(() => {
    setSelectedSetId(null);
  }, [selectedCaseId]);

  // Also initialize it on mount
  useEffect(() => {
    // Set initial sidebar width
    const initialWidth = isSidebarCollapsed ? '48px' : '224px';
    document.documentElement.style.setProperty('--sidebar-width', initialWidth);
  }, [isSidebarCollapsed]);

  useEffect(() => {
    console.log('shouldHighlightNewCaseButton changed to:', shouldHighlightNewCaseButton);
  }, [shouldHighlightNewCaseButton]);

  useEffect(() => {
    const hasCases = cases.length > 0;
    
    // Turn off highlight when cases exist
    if (hasCases && shouldHighlightNewCaseButton) {
      console.log('CaseManager - Turning off button highlight because cases exist');
      setShouldHighlightNewCaseButton(false);
    }
  }, [cases.length, shouldHighlightNewCaseButton]);

  // Export handler - now properly triggers the EvidenceManager's export function
  const handleExportCombinedPdf = useCallback(() => {
    console.log('CaseManager - Export triggered from header');
    console.log('🔧 CaseManager - Debug ref:', {
      hasExportTriggerRef: !!exportTriggerRef,
      hasRefCurrent: !!exportTriggerRef.current,
      refCurrentType: typeof exportTriggerRef.current
    });
    
    // Call the actual export function from EvidenceManager via ref
    if (exportTriggerRef.current) {
      console.log('✅ CaseManager - Calling EvidenceManager export function');
      exportTriggerRef.current();
    } else {
      console.warn('⚠️ Export function not available - EvidenceManager not ready');
    }
  }, []);

  // Callback to update export state from EvidenceManager
  const handleExportStateChange = useCallback((exportState) => {
    setIsExporting(exportState.isExporting || false);
    setCanExport(exportState.canExport || false);
    setHasErrors(exportState.hasErrors || false);
  }, []);

  // Auto-exit filter mode when all errors are resolved
  useEffect(() => {
    if (!hasErrors && filterErrors) {
      setFilterErrors(false);
    }
  }, [hasErrors]);

  // Load cases
  const loadCases = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    try {
      console.log('CaseManager - Loading cases for user:', currentUser.uid);
      setIsLoading(true);
      
      const userCases = await FirebaseService.getUserCases(currentUser.uid);
      console.log('CaseManager - Loaded cases:', userCases);
      
      setCases(userCases);
      
      const storedCaseId = localStorage.getItem('selectedCaseId');
      
      if (storedCaseId && userCases.some(c => c.id === storedCaseId)) {
        setSelectedCaseId(storedCaseId);
      } else if (userCases.length > 0) {
        setSelectedCaseId(userCases[0].id);
      }
    } catch (error) {
      console.error('CaseManager - Error loading cases:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.uid, setSelectedCaseId]);

  useEffect(() => {
    if (selectedCase && selectedCase.stampConfig) {
      console.log('CaseManager - Loading stamp config for case:', selectedCase.id);
      const validatedConfig = EvidenceDataService.validateStampConfig(selectedCase.stampConfig);
      setStampConfig(validatedConfig);
    } else if (selectedCase) {
      // If case doesn't have stampConfig yet, use defaults
      console.log('CaseManager - No stamp config found, using defaults');
      const defaultConfig = EvidenceDataService.validateStampConfig({});
      setStampConfig(defaultConfig);
    }
  }, [selectedCase]);

  // Load cases when user changes
  useEffect(() => {
    loadCases();
  }, [loadCases]);

  // Handle stamp config changes
  const handleStampConfigChange = useCallback((newConfig) => {
    console.log('CaseManager - Stamp config changed:', newConfig);
    setStampConfig(newConfig);

    trackStampConfigChanged(selectedCase?.id, newConfig);
    
    // Save to Firebase if we have a selected case
    if (selectedCase?.id && currentUser?.uid) {
      FirebaseService.updateCase(
        selectedCase.id, 
        { stampConfig: newConfig }, 
        currentUser.uid
      ).catch(error => {
        console.error('CaseManager - Error saving stamp config:', error);
      });
    }
  }, [selectedCase, currentUser?.uid]);

  // Handle updating case
  const handleUpdateCase = useCallback(async (updates) => {
    if (!currentUser?.uid || !selectedCase) return;
    
    try {
      console.log('CaseManager - Updating case:', selectedCase.id, updates);
      await FirebaseService.updateCase(selectedCase.id, updates, currentUser.uid);
      
      trackCaseEdited(selectedCase.id);

      // Update local state
      setCases(prev => prev.map(c => 
        c.id === selectedCase.id ? { ...c, ...updates } : c
      ));

      // Close modal if it was open
      setIsEditCaseModalOpen(false);

      console.log('CaseManager - Case updated successfully');
    } catch (error) {
      console.error('CaseManager - Error updating case:', error);
    }
  }, [currentUser?.uid, selectedCase]);

  const handleCaseModified = useCallback(() => {
    if (!selectedCase?.id) return;
    
    console.log('CaseManager - Refreshing case lastModified timestamp');
    
    // Update the lastModified timestamp for this case in local state
    setCases(prev => prev.map(c => 
      c.id === selectedCase.id 
        ? { ...c, lastModified: new Date() } 
        : c
    ));
  }, [selectedCase?.id]);

  // Handle archiving case
  const handleArchiveCase = useCallback(async (caseId) => {
    if (!currentUser?.uid) return;
    
    const targetCaseId = caseId || selectedCase?.id;
    if (!targetCaseId) return;
    
    try {
      console.log('CaseManager - Archiving case:', targetCaseId);
      
      const caseToArchive = cases.find(c => c.id === targetCaseId);
      const isCurrentlyArchived = caseToArchive?.archived || false;
      
      await FirebaseService.updateCase(
        targetCaseId,
        { archived: !isCurrentlyArchived },
        currentUser.uid
      );
      await incrementActionCount(currentUser.uid, isCurrentlyArchived ? 'restoreCase' : 'archiveCase');

      // Update local state
      setCases(prev => prev.map(c => 
        c.id === targetCaseId ? { ...c, archived: !isCurrentlyArchived } : c
      ));
      
      console.log(`CaseManager - Case ${isCurrentlyArchived ? 'restored' : 'archived'} successfully`);
    } catch (error) {
      console.error('CaseManager - Error archiving case:', error);
    }
  }, [currentUser?.uid, selectedCase, cases]);

  // Handle deleting case
  const handleDeleteCase = useCallback(async (caseId) => {
    if (!currentUser?.uid) return;
    
    try {
      console.log('CaseManager - Deleting case:', caseId);
      await FirebaseService.deleteCase(caseId, currentUser.uid);
      
      trackCaseDeleted(caseId);

      // Update local state
      setCases(prev => prev.filter(c => c.id !== caseId));
      
      // Clear selection if this case was selected
      if (selectedCaseId === caseId) {
        const remainingCases = cases.filter(c => c.id !== caseId);
        setSelectedCaseId(remainingCases.length > 0 ? remainingCases[0].id : null);
      }
      
      console.log('CaseManager - Case deleted successfully');
    } catch (error) {
      console.error('CaseManager - Error deleting case:', error);
    }
  }, [currentUser?.uid, cases, selectedCaseId, setSelectedCaseId]);

  // Handle reordering cases
  const handleReorderCases = useCallback(async (orderUpdates) => {
    if (!Array.isArray(orderUpdates)) return;
    
    try {
      console.log('CaseManager - Reordering cases:', orderUpdates);
      
      // Update Firebase with new order
      const batch = [];
      for (const update of orderUpdates) {
        batch.push(
          FirebaseService.updateCase(update.caseId, { order: update.order }, currentUser.uid)
        );
      }
      
      await Promise.all(batch);
      
      // Update local state
      setCases(prev => {
        const updated = [...prev];
        orderUpdates.forEach(update => {
          const caseIndex = updated.findIndex(c => c.id === update.caseId);
          if (caseIndex !== -1) {
            updated[caseIndex] = { ...updated[caseIndex], order: update.order };
          }
        });
        
        // Re-sort by order
        return updated.sort((a, b) => (a.order || 0) - (b.order || 0));
      });
      
      console.log('CaseManager - Cases reordered successfully');
    } catch (error) {
      console.error('CaseManager - Error reordering cases:', error);
    }
  }, [currentUser?.uid]);

  // Handle adding new case
  const handleAddCase = useCallback(async (newCaseData) => {
    if (!currentUser?.uid) return;
    
    try {
      console.log('CaseManager - Creating new case:', newCaseData);
      const newCase = await FirebaseService.createCase(newCaseData, currentUser.uid);
      trackCaseCreated(newCase.id);
      await incrementActionCount(currentUser.uid, 'createCase');

      // Add to local state
      setCases(prev => [newCase, ...prev]);
      setSelectedCaseId(newCase.id);
      
      console.log('CaseManager - New case created successfully:', newCase.id);
    } catch (error) {
      console.error('CaseManager - Error creating case:', error);
    }
  }, [currentUser?.uid, setSelectedCaseId]);

  // Handle authentication success
  const handleAuthSuccess = useCallback(() => {
    console.log('CaseManager - Authentication successful, will load cases');
    // Cases will be loaded automatically when currentUser changes via useEffect
  }, []);

  const [tagGuideOpenForCaseId, setTagGuideOpenForCaseId] = useState(null);
  const tagGuideVisible = tagGuideOpenForCaseId === selectedCaseId && tagGuideOpenForCaseId !== null;
  const [tagGuideStep, setTagGuideStep] = useState(1);
  const [tagGuideTriggerEl, setTagGuideTriggerEl] = useState(null);
  const [tagGuideFromDropdown, setTagGuideFromDropdown] = useState(false);
  // Separate from tagGuideStep so the sidebar can open before the guide moves to step 2
  const [sidebarNeedsOpen, setSidebarNeedsOpen] = useState(false);
  const sidebarOpenTimerRef = useRef(null);

  // True only on step 1 when the guide was started from the export dropdown —
  // keeps the popover visually open so the guide can point at the ? button's position.
  const keepDropdownOpenForGuide = tagGuideVisible && tagGuideStep === 1 && tagGuideFromDropdown;

  // Wipe guide state on any case switch so it cannot reopen if the user switches back
  useEffect(() => {
    setTagGuideOpenForCaseId(null);
  }, [selectedCaseId]);

  const handleStartTagGuide = useCallback((triggerEl, fromDropdown = false) => {
    setTagGuideTriggerEl(triggerEl || null);
    setTagGuideStep(1);
    setTagGuideFromDropdown(fromDropdown);
    setSidebarNeedsOpen(false);  // reset so forceExpanded can transition false→true on step 2
    setTagGuideOpenForCaseId(selectedCaseId);
    if (currentUser?.uid) {
      incrementActionCount(currentUser.uid, 'tagGuideOpened');
    }
  }, [selectedCaseId, currentUser?.uid]);

  // Navigate the guide to a specific step, opening the sidebar first if step 2 needs it.
  // If the sidebar is collapsed, the sidebar opens immediately and the guide waits for the
  // 200ms animation before repositioning, so the guide travels in a straight line.
  const navigateGuideToStep = useCallback((newStep) => {
    clearTimeout(sidebarOpenTimerRef.current);
    if (newStep === 2 && isSidebarCollapsed) {
      setSidebarNeedsOpen(true);
      sidebarOpenTimerRef.current = setTimeout(() => setTagGuideStep(newStep), 250);
    } else {
      if (newStep === 2) setSidebarNeedsOpen(true);
      setTagGuideStep(newStep);
    }
  }, [isSidebarCollapsed]);

  const handleTagGuideNext = useCallback(() => {
    navigateGuideToStep(Math.min(tagGuideStep + 1, 5));
  }, [tagGuideStep, navigateGuideToStep]);

  const handleTagGuideStepChange = useCallback((newStep) => {
    navigateGuideToStep(newStep);
  }, [navigateGuideToStep]);

  const handleTagGuideClose = useCallback(() => {
    setTagGuideOpenForCaseId(null);
  }, []);

  const handleTagGuideGotIt = useCallback(() => {
    setTagGuideOpenForCaseId(null);
  }, []);

  if (!currentUser || !isUserVerified()) {
    return <AuthPage onAuthSuccess={handleAuthSuccess} />;
  }

  return (
    <GuideForceProvider tagGuideStep={tagGuideVisible ? tagGuideStep : null}>
    <div className="flex h-screen">
      {/* Sidebar */}
      <CasesSidebar
        cases={cases}
        selectedCaseId={selectedCaseId}
        onSelectCase={setSelectedCaseId}
        onAddCase={handleAddCase}
        onDeleteCase={handleDeleteCase}
        onArchiveCase={handleArchiveCase}
        onReorderCases={handleReorderCases}
        isLoading={isLoading}
        currentUser={currentUser}
        onLogout={logout}
        onCollapsedChange={handleSidebarCollapsedChange}
        forceExpanded={sidebarNeedsOpen}
        newCaseButtonRef={newCaseButtonRef}
        highlightNewCaseButton={shouldHighlightNewCaseButton}
        documentSets={selectedCase?.documentSets || []}
        selectedSetId={selectedSetId}
        onSelectSet={handleSelectSet}
        onToggleArchiveSet={handleToggleArchiveSet}
        onDeleteSet={handleDeleteSet}
        onCreateDocumentSet={handleCreateDocumentSet}
        onUpdateSetName={handleUpdateSetName}
      />

      {/* Main content */}
      <div 
        className="flex-1 flex flex-col transition-all duration-300"
        style={{
          marginLeft: isSidebarCollapsed ? '48px' : '224px'
        }}
      >
        {/* NEW: Trial Banner */}
        <TrialBanner
          userId={currentUser?.uid}
        />
        <div className="flex-1 min-h-0">
          {selectedCase ? (
            
            <div 
              className="h-full flex flex-col bg-app-white"
            >
              {/* Header with case name and modal button */}
              <div className="flex-shrink-0 px-app-3xl pt-app-2xl pb-0">
                <div className="pb-app-lg border-b border-app-gray-400">
                  <CaseName
                    caseName={selectedCase.name}
                    caseData={selectedCase}
                    onOpenEditModal={() => setIsEditCaseModalOpen(true)}
                    isExporting={isExporting}
                    canExport={canExport}
                    onExportCombinedPdf={handleExportCombinedPdf}
                    selectedSet={selectedSet}
                    documentSets={selectedCase?.documentSets || []}
                    onSelectSet={handleSelectSet}
                    inTagTeachingMode={inTagTeachingMode}
                    keepDropdownOpenForGuide={keepDropdownOpenForGuide}
                    onStartTagGuide={handleStartTagGuide}
                    hasErrors={hasErrors}
                    filterErrors={filterErrors}
                    onToggleFilterErrors={() => setFilterErrors(f => !f)}
                  />
                </div>
              </div>
              
              {/* Main content with Evidence Manager */}
              <div className="flex-1 min-h-0 overflow-hidden">
                <EvidenceManager
                  caseId={selectedCaseId}
                  stampConfig={stampConfig}
                  onStampConfigChange={handleStampConfigChange}
                  caseData={selectedCase}
                  onCaseModified={handleCaseModified}
                  // Export props
                  onExportStateChange={handleExportStateChange}
                  exportTriggerRef={exportTriggerRef}
                  selectedSetId={selectedSetId}
                  // Document sets props - SINGLE SOURCE OF TRUTH
                  documentSets={selectedCase?.documentSets || []}
                  onDocumentSetsChange={setCases}
                  // UI preferences
                  isSetsCollapsed={isSetsCollapsed}
                  onSetsCollapsedChange={handleSetCollapsedChange}
                  filterErrors={filterErrors}
                  // Archive handler
                  onArchiveCase={handleArchiveCase}
                  onStartTagGuide={handleStartTagGuide}
                />
              </div>
            </div>
          ) : (
            /* No case selected - also flattened */
            <div className="h-full flex items-center justify-center bg-app-white">
              {isLoading ? (
              <div className="text-app-gray-600">
                Loading...
              </div>
              ) : (
                <NoCaseSelected 
                  onCreateNewCase={() => setIsNewCaseModalOpen(true)} 
                  hasCases={cases.length > 0}
                  newCaseButtonRef={newCaseButtonRef}
                  setShouldHighlightNewCaseButton={setShouldHighlightNewCaseButton}
                />
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* New case modal */}
      <NewCaseModal
        isOpen={isNewCaseModalOpen}
        onClose={() => setIsNewCaseModalOpen(false)}
        onAddCase={handleAddCase}
        mode="create"
      />
      {/* Edit case modal */}
      <NewCaseModal
        isOpen={isEditCaseModalOpen}
        onClose={() => setIsEditCaseModalOpen(false)}
        onUpdateCase={handleUpdateCase}
        existingCase={selectedCase}
        mode="edit"
      />
    </div>

    <TagGuide
      visible={tagGuideVisible}
      step={tagGuideStep}
      triggerEl={tagGuideTriggerEl}
      onNext={handleTagGuideNext}
      onGotIt={handleTagGuideGotIt}
      onClose={handleTagGuideClose}
      onStepChange={handleTagGuideStepChange}
    />
  </GuideForceProvider>
  );
};

export default CaseManager;